import { create } from "zustand";
import { fetchApi } from "@/lib/api";
import { captureException } from "@/lib/analytics";
import { useQuoteSettingsStore } from "@/store/quoteSettingsStore";
import { useItemSettingsStore } from "@/store/itemSettingsStore";
import {
  ERPIntegrationProviderMetadata,
  ERPIntegrationProvidersResponseSchema,
  ERPIntegrationSync,
  ERPIntegrationSyncResponseSchema,
  ERPIntegrationSyncStatus,
  ERPIntegrationSyncsResponseSchema,
  ERPProviderKey,
} from "@forgepoint/shared";

export type {
  ERPIntegrationProviderMetadata,
  ERPIntegrationSync,
  ERPIntegrationSyncStatus,
  ERPProviderKey,
} from "@forgepoint/shared";

interface ERPIntegrationsState {
  providers: ERPIntegrationProviderMetadata[];
  providersLoading: boolean;
  providersError: string | null;

  syncs: ERPIntegrationSync[];
  syncsLoading: boolean;
  syncsError: string | null;

  fetchProviders: () => Promise<void>;
  fetchSyncs: () => Promise<void>;
  createSync: (input: {
    erpIntegrationProviderId: ERPProviderKey;
    connectionId: string;
    status?: ERPIntegrationSyncStatus;
    integrationConfig?: Record<string, unknown> | null;
    autoPushEnabled?: boolean;
  }) => Promise<{ ok: true } | { ok: false; error: string }>;
  updateSync: (
    id: string,
    input: {
      status?: ERPIntegrationSyncStatus;
      integrationConfig?: Record<string, unknown> | null;
      autoPushEnabled?: boolean;
    },
  ) => Promise<boolean>;
  deleteSync: (id: string) => Promise<boolean>;
}

export const useERPIntegrationsStore = create<ERPIntegrationsState>(
  (set, get) => ({
    providers: [],
    providersLoading: false,
    providersError: null,
    syncs: [],
    syncsLoading: false,
    syncsError: null,

    fetchProviders: async () => {
      set({ providersLoading: true, providersError: null });

      try {
        const response = await fetchApi("integrations/erp/providers", "GET");

        if (!response.ok || !response.data) {
          set({
            providersLoading: false,
            providersError: "Failed to fetch ERP integrations",
          });
          return;
        }

        const data = ERPIntegrationProvidersResponseSchema.parse(response.data);
        set({ providers: data.providers, providersLoading: false });
      } catch (error) {
        captureException(error as Error);
        set({
          providersLoading: false,
          providersError:
            error instanceof Error ? error.message : "Unknown error",
        });
      }
    },

    fetchSyncs: async () => {
      set({ syncsLoading: true, syncsError: null });

      try {
        const response = await fetchApi("integrations/erp/syncs", "GET");

        if (!response.ok || !response.data) {
          set({
            syncsLoading: false,
            syncsError: "Failed to fetch ERP integration syncs",
          });
          return;
        }

        const data = ERPIntegrationSyncsResponseSchema.parse(response.data);
        set({ syncs: data.syncs, syncsLoading: false });
      } catch (error) {
        captureException(error as Error);
        set({
          syncsLoading: false,
          syncsError: error instanceof Error ? error.message : "Unknown error",
        });
      }
    },

    createSync: async (input) => {
      try {
        const response = await fetchApi("integrations/erp/syncs", "POST", {
          body: input,
        });

        if (!response.ok || !response.data) {
          const errorData = response.data as { error?: string } | undefined;
          return {
            ok: false,
            error: errorData?.error || "Failed to create ERP sync",
          };
        }

        const data = ERPIntegrationSyncResponseSchema.parse(response.data);
        set({
          syncs: [...get().syncs, data.sync],
        });
        refetchErpLockedSettings();
        return { ok: true };
      } catch (error) {
        captureException(error as Error);
        const message =
          error instanceof Error ? error.message : "Unknown error";
        return { ok: false, error: message };
      }
    },

    updateSync: async (id, input) => {
      set({ syncsError: null });
      try {
        const response = await fetchApi(
          `integrations/erp/syncs/${id}`,
          "PATCH",
          {
            body: input,
          },
        );

        if (!response.ok || !response.data) {
          const errorData = response.data as { error?: string } | undefined;
          set({
            syncsError: errorData?.error || "Failed to update ERP sync",
          });
          return false;
        }

        const data = ERPIntegrationSyncResponseSchema.parse(response.data);
        set({
          syncs: get().syncs.map((sync) => (sync.id === id ? data.sync : sync)),
        });
        refetchErpLockedSettings();
        return true;
      } catch (error) {
        captureException(error as Error);
        set({
          syncsError: error instanceof Error ? error.message : "Unknown error",
        });
        return false;
      }
    },

    deleteSync: async (id) => {
      set({ syncsError: null });
      try {
        const response = await fetchApi(
          `integrations/erp/syncs/${id}`,
          "DELETE",
        );

        if (response.ok) {
          set({
            syncs: get().syncs.filter((sync) => sync.id !== id),
          });
          refetchErpLockedSettings();
          return true;
        }

        const errorData = response.data as { error?: string } | undefined;
        set({
          syncsError: errorData?.error || "Failed to delete ERP sync",
        });
        return false;
      } catch (error) {
        captureException(error as Error);
        set({
          syncsError: error instanceof Error ? error.message : "Unknown error",
        });
        return false;
      }
    },
  }),
);

/**
 * Force-refetches the quote and item settings, since ERP sync changes alter which settings are locked.
 * @returns Nothing; the refetches run in the background
 */
function refetchErpLockedSettings(): void {
  useQuoteSettingsStore.getState().fetchSettings({ force: true });
  useItemSettingsStore.getState().fetchSettings({ force: true });
}
